/**
 * TeleTime POS — keeps the cart's EHF in step with the server.
 *
 * Every cart line's per-unit EHF comes from POST /api/tax/ehf/resolve, the resolver the
 * recorder charges with. This watches the set of lines the server hasn't answered for yet
 * (store.getUnresolvedEhfLines — keyed by province, product and line kind) and asks about
 * them as soon as the cart settles, whatever put them there: search, the grid, a scan, a
 * quote, a recalled or restored cart. The answer is normally in long before tender opens;
 * tenderGate holds tender until it is.
 *
 * One request at a time. Lines added while one is in flight are asked about when it lands.
 * A failure is recorded as `ehfStatus: 'unreachable'` and not retried on a timer — the next
 * cart change or store.retryEhf() (the checkout banner's Retry) asks again.
 */

import { useUnifiedStore } from './unifiedStore';
import { resolveEhf } from '../api/tax';

/** Re-ask whenever the unanswered lines, or the retry token, change. */
const signature = (st) =>
  `${st.getUnresolvedEhfLines().map((l) => l.key).join('|')}#${st.ehfRetryToken}`;

/**
 * @param {Object} [opts]
 * @param {typeof useUnifiedStore} [opts.store]
 * @param {typeof resolveEhf} [opts.resolve]
 * @param {number} [opts.debounceMs]
 * @returns {() => void} stop
 */
export function startEhfSync({ store = useUnifiedStore, resolve = resolveEhf, debounceMs = 100 } = {}) {
  let timer = null;
  let inFlight = false;
  let stopped = false;

  const schedule = () => {
    if (stopped) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(run, debounceMs);
  };

  async function run() {
    timer = null;
    if (stopped || inFlight) return;

    const st = store.getState();
    const pending = st.getUnresolvedEhfLines();
    if (pending.length === 0) {
      if (st.ehfStatus !== 'ok') st.setEhfStatus('ok');
      return;
    }

    // The answer is filed under the province asked about, even if the cart's province
    // changes while the request is out.
    const province = st.taxProvince;
    inFlight = true;
    st.setEhfStatus('loading');

    let res;
    try {
      res = await resolve(pending.map(({ productId, name, sku }) => ({ productId, name, sku })), province);
    } catch (err) {
      res = { success: false, error: err?.message };
    }
    inFlight = false;
    if (stopped) return;

    const now = store.getState();
    if (!res?.success) {
      now.setEhfStatus('unreachable');
      return;
    }

    const answered = res.data?.lines || [];
    const entries = [];
    pending.forEach((p, index) => {
      const line = answered.find((l) => l.index === index);
      if (line && Number.isInteger(line.ehfCents)) entries.push({ key: p.key, ehfCents: line.ehfCents });
    });
    now.setServerEhf(entries);
    now.setEhfStatus(entries.length === pending.length ? 'ok' : 'unreachable');

    // Anything added (or a province change) while the request was out.
    schedule();
  }

  const unsubscribe = store.subscribe(signature, schedule);
  schedule();

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    unsubscribe();
  };
}

export default startEhfSync;
